import {
  apiAddNote,
  apiDeleteNote,
  apiGetNotes,
  apiUpdateNote,
} from "@lib/api/note";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useIntl from "react-intl/lib/src/components/useIntl";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import { NotesModal } from "@lib/components/PageComponents/Notes";
import { ApiParams } from "@lib/types/Api";
import { FormattedMessage } from "react-intl";
import { toast } from "react-toastify";
import { Note } from "@lib/types/Note";
import { Invoice } from "@lib/types/Billing";
import getColumns from "./columns";

type Props = {
  invoice: Invoice;
};

export default function InvoiceNotes({ invoice }: Props) {
  const { messages } = useIntl();
  const [params, setParams] = useState<ApiParams>({
    page: 1,
    limit: 3,
    sort: "createdAt,DESC",
  });
  const query = useQuery({
    queryFn: () =>
      apiGetNotes({
        filter: [`invoiceId||$eq||${invoice?.id}`],
        join: ["customer", "product", "invoice"],
        ...params,
      }),
    queryKey: ["invoice-notes", params],
    enabled: Boolean(invoice?.id),
    refetchOnWindowFocus: true,
  });

  const { data, isLoading, isFetching } = query;
  const apiData = data?.data?.data;

  useEffect(() => {
    if (!invoice?.id) {
      queryClient.removeQueries({
        queryKey: ["invoice-notes", params],
      });
    } else {
      query.refetch();
    }
  }, [invoice?.id]);

  const queryClient = useQueryClient();
  const addQuery = useMutation({
    mutationFn: apiAddNote,
    mutationKey: ["invoice-notes"],
    onSuccess() {
      toast.success(
        <FormattedMessage
          id="common.createdMessage"
          values={{ name: messages["common.note"] as string }}
        />,
      );
      queryClient.invalidateQueries({
        queryKey: ["invoice-notes"],
      });
      toggle(false);
    },
  });
  const updateQuery = useMutation({
    mutationFn: apiUpdateNote,
    mutationKey: ["invoice-notes"],
    onSuccess() {
      toast.success(
        <FormattedMessage
          id="common.updatedMessage"
          values={{ name: messages["common.note"] as string }}
        />,
      );
      queryClient.invalidateQueries({
        queryKey: ["invoice-notes"],
      });
      toggle(false);
    },
  });
  const deleteQuery = useMutation({
    mutationFn: apiDeleteNote,
    mutationKey: ["invoice-notes"],
    onSuccess() {
      toast.success(
        <FormattedMessage
          id="common.deletedMessage"
          values={{ name: messages["common.note"] as string }}
        />,
      );
      queryClient.invalidateQueries({
        queryKey: ["invoice-notes"],
      });
      toggle(false);
    },
  });

  const [openModal, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<Note>();

  useEffect(() => {
    if (!openModal) setSelected(null);
  }, [openModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelect = (item: Note) => {
    setSelected(item);
    toggle(true);
  };

  const submitQuery = selected ? updateQuery : addQuery;

  const columns = getColumns({
    apiData,
    messages,
    deleteQuery,
    handleSelect,
  });

  return (
    <>
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 3}
          onPageChange={(page: number, limit: number) => {
            setParams({ ...params, page, limit });
          }}
          query={query}
          plusProps={{
            onClick: () => toggle(true),
            disabled: isLoading,
            loading: isLoading,
          }}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          rowKey={"id"}
          loading={isLoading || isFetching}
          dataSource={apiData}
          columns={columns}
        />
      </AppsContent>

      <NotesModal
        query={query}
        mutationQuery={submitQuery}
        deleteQuery={deleteQuery}
        openModal={openModal}
        toggleModal={toggle}
        customer={invoice?.customer}
        invoice={invoice}
        selected={selected}
      />
    </>
  );
}
